package com.evoapps.evoshift.widgets

import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.IconButton
import androidx.compose.material3.IconButtonColors
import androidx.compose.material3.IconButtonDefaults
import androidx.compose.material3.PlainTooltip
import androidx.compose.material3.Text
import androidx.compose.material3.TooltipAnchorPosition
import androidx.compose.material3.TooltipBox
import androidx.compose.material3.TooltipDefaults
import androidx.compose.material3.rememberTooltipState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.input.pointer.PointerEventType
import androidx.compose.ui.input.pointer.PointerType
import androidx.compose.ui.input.pointer.pointerInput
import kotlinx.coroutines.launch

/**
 * SH-161 (F6): an icon-only button whose [tooltip] (the icon's content description) shows on
 * hover with a mouse or a trackpad (Mac, web, Android laptops), as on the web. The tooltip sits
 * inside the button, so [modifier] (alignment, weight, test tags, the hand cursor) stays the
 * button's own as with a plain [IconButton]. A null or blank [tooltip] is a plain [IconButton].
 */
@Composable
fun IconButtonWithTooltip(
    onClick: () -> Unit,
    tooltip: String?,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    colors: IconButtonColors = IconButtonDefaults.iconButtonColors(),
    interactionSource: MutableInteractionSource? = null,
    content: @Composable () -> Unit
) {
    IconButton(onClick = onClick, modifier = modifier, enabled = enabled, colors = colors, interactionSource = interactionSource) {
        if (tooltip.isNullOrBlank()) content() else HoverTooltip(tooltip, content)
    }
}

/**
 * [text] as a plain tooltip over [content], which fills its parent (the whole button reacts to the
 * hover). Only a mouse or a trackpad shows it: Material's own gestures (enableUserInput) would put
 * a semantics node of their own between the button and its icon, and the button would lose its
 * description for screen readers and the UI tests.
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun HoverTooltip(text: String, content: @Composable () -> Unit) {
    val state = rememberTooltipState()
    val scope = rememberCoroutineScope()
    TooltipBox(
        positionProvider = TooltipDefaults.rememberTooltipPositionProvider(TooltipAnchorPosition.Above),
        tooltip = { PlainTooltip { Text(text) } },
        state = state,
        modifier = Modifier.fillMaxSize(),
        enableUserInput = false
    ) {
        Box(
            Modifier.fillMaxSize().pointerInput(text) {
                awaitPointerEventScope {
                    while (true) {
                        // Nothing is consumed: the click still reaches the button.
                        val event = awaitPointerEvent()
                        if (event.changes.none { it.type == PointerType.Mouse }) continue
                        when (event.type) {
                            PointerEventType.Enter -> scope.launch { state.show() }
                            PointerEventType.Exit, PointerEventType.Press -> state.dismiss()
                            else -> Unit
                        }
                    }
                }
            },
            contentAlignment = Alignment.Center
        ) { content() }
    }
}
